import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Hash, Lock, Search } from 'lucide-react';
import { waysApi, type WaysChannel } from '../../services/waysApi';

/**
 * Every channel this person can read, in one list (hs-pmo/ep43#12: "the main page with all the
 * channels"). The sidebar is the person's own arrangement; this is the directory, filterable by
 * name, key or project. Ways decides what is listed; choosing one opens it in the page.
 */
export default function WaysChannels({ active, projectName, onOpen, onError }: {
  active: string | null;
  projectName: (id?: string | null) => string;
  onOpen: (key: string) => void;
  onError: (e: unknown) => void;
}) {
  const [channels, setChannels] = useState<WaysChannel[] | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    waysApi.channels().then((r) => setChannels(r.channels || [])).catch((e) => { setChannels([]); onError(e); });
  }, [onError]);

  // Grouped by kind, so General, project and epic channels do not interleave.
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const by: Record<string, WaysChannel[]> = {};
    for (const c of channels || []) {
      const hay = `${c.name} ${c.key} ${projectName(c.projectId)}`.toLowerCase();
      if (needle && !hay.includes(needle)) continue;
      (by[c.kind || 'other'] ||= []).push(c);
    }
    for (const list of Object.values(by)) list.sort((a, b) => a.name.localeCompare(b.name));
    return Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
  }, [channels, q, projectName]);

  return (
    <>
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
        <label className="relative block">
          <span className="sr-only">Filter channels</span>
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name, key or project"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent" />
        </label>
      </div>
      <div className="flex-1 overflow-y-auto">
        {channels === null ? (
          // First paint: a skeleton, and the standard's one loading word.
          <div className="px-4 py-3 space-y-2" aria-busy="true">
            <span className="sr-only">Loading…</span>
            {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-8 rounded-lg bg-gray-100 dark:bg-gray-800 motion-safe:animate-pulse" />)}
          </div>
        ) : !groups.length ? (
          <p className="px-4 py-10 text-center text-sm text-gray-500">
            {q ? 'No channel matches that filter. Clear it to see every channel.'
              : 'Channels you can read appear here: General, and one for each project and epic you work in.'}
          </p>
        ) : groups.map(([kind, list]) => (
          <section key={kind} className="py-2">
            <h3 className="px-4 pb-1 text-[11px] uppercase tracking-wide text-gray-400">{kind} · {list.length}</h3>
            <ul>
              {list.map((c) => (
                <li key={c.key}>
                  <button type="button" onClick={() => onOpen(c.key)}
                    aria-current={active === c.key ? 'true' : undefined}
                    className={clsx('w-full flex items-center gap-2 px-4 py-1.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                      active === c.key && 'bg-accent/10 text-accent')}>
                    {c.readOnly
                      ? <Lock className="w-3.5 h-3.5 shrink-0 text-gray-400" aria-label="Read-only" />
                      : <Hash className="w-3.5 h-3.5 shrink-0 text-gray-400" aria-hidden="true" />}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-gray-800 dark:text-gray-100">{c.name}</span>
                      <span className="block truncate text-xs text-gray-500 font-mono">
                        {c.key}{c.projectId ? ` · ${projectName(c.projectId)}` : ''}
                      </span>
                    </span>
                    {c.unread ? (
                      <span className="shrink-0 px-1.5 rounded-full bg-accent text-white text-[11px]">
                        {c.unread}<span className="sr-only"> unread</span>
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
}
